import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import Sidebar from "../components/Sidebar";

export default function AdminDashboard() {
  const [vehicles, setVehicles] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const [vehiclesSnap, reservationsSnap, brandsSnap, featuredSnap] =
          await Promise.all([
            getDocs(collection(db, "vehicules_auto")),
            getDocs(collection(db, "reservations")),
            getDocs(collection(db, "marques")),
            getDoc(doc(db, "homepage_sections", "featured_choices")),
          ]);

        const vehiclesData = vehiclesSnap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        const reservationsData = reservationsSnap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        const brandsData = brandsSnap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        setVehicles(vehiclesData);
        setReservations(reservationsData);
        setBrands(brandsData);

        if (featuredSnap.exists()) {
          const featuredData = featuredSnap.data();
          setFeaturedIds(featuredData.vehicleIds || []);
        } else {
          setFeaturedIds([]);
        }
      } catch (error) {
        console.error("Erreur chargement dashboard :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const stats = useMemo(() => {
    const totalVehicles = vehicles.length;
    const vehiclesEnVente = vehicles.filter((v) => v.enVente === true).length;
    const vehiclesEnLocation = vehicles.filter(
      (v) => v.location?.estDisponible === true
    ).length;
    const vehiclesDisponibles = vehicles.filter(
      (v) => v.statut === "disponible"
    ).length;

    const reservationsEnAttente = reservations.filter(
      (r) =>
        r.statut === "en attente" ||
        r.statut === "en attente de validation"
    ).length;

    const reservationsValidees = reservations.filter(
      (r) => r.statut === "validée"
    ).length;

    const vehiclesSansImage = vehicles.filter(
      (v) => !Array.isArray(v.imageUrls) || v.imageUrls.length === 0
    ).length;

    const vehiclesSansPrix = vehicles.filter(
      (v) => !v.prix || Number(v.prix) <= 0
    ).length;

    return {
      totalVehicles,
      vehiclesEnVente,
      vehiclesEnLocation,
      vehiclesDisponibles,
      reservationsEnAttente,
      reservationsValidees,
      totalBrands: brands.length,
      featuredCount: featuredIds.length,
      vehiclesSansImage,
      vehiclesSansPrix,
    };
  }, [vehicles, reservations, brands, featuredIds]);

  const featuredVehicles = useMemo(() => {
    return featuredIds
      .map((id) => vehicles.find((v) => v.id === id))
      .filter(Boolean);
  }, [featuredIds, vehicles]);

  const recentReservations = useMemo(() => {
    return [...reservations]
      .sort((a, b) => {
        const da = a.createdAt?.seconds
          ? a.createdAt.seconds * 1000
          : new Date(a.createdAt || 0).getTime();
        const dbb = b.createdAt?.seconds
          ? b.createdAt.seconds * 1000
          : new Date(b.createdAt || 0).getTime();
        return dbb - da;
      })
      .slice(0, 6);
  }, [reservations]);

  const recentVehicles = useMemo(() => {
    return [...vehicles]
      .sort((a, b) => {
        const da = a.createdAt?.seconds
          ? a.createdAt.seconds * 1000
          : new Date(a.createdAt || 0).getTime();
        const dbb = b.createdAt?.seconds
          ? b.createdAt.seconds * 1000
          : new Date(b.createdAt || 0).getTime();
        return dbb - da;
      })
      .slice(0, 6);
  }, [vehicles]);

  const alerts = useMemo(() => {
    const items = [];

    if (stats.reservationsEnAttente > 0) {
      items.push({
        type: "warning",
        text: `${stats.reservationsEnAttente} réservation(s) en attente de validation.`,
      });
    }

    if (stats.vehiclesSansImage > 0) {
      items.push({
        type: "danger",
        text: `${stats.vehiclesSansImage} véhicule(s) sans image.`,
      });
    }

    if (stats.vehiclesSansPrix > 0) {
      items.push({
        type: "secondary",
        text: `${stats.vehiclesSansPrix} véhicule(s) sans prix ou prix invalide.`,
      });
    }

    if (stats.featuredCount !== 3) {
      items.push({
        type: "info",
        text: `La section "véhicules à mettre en avant" ne contient pas encore 3 véhicules.`,
      });
    }

    return items;
  }, [stats]);

  const formatPrice = (prix) => {
    const value = Number(prix);
    if (!value || value <= 0) return "Prix à la demande";
    return `${value.toLocaleString("fr-FR")} FCFA`;
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const date = value?.seconds
      ? new Date(value.seconds * 1000)
      : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("fr-FR");
  };

  if (loading) {
    return (
      <div className="d-flex flex-column flex-md-row">
        <Sidebar />
        <main className="flex-grow-1 p-4" style={{ marginLeft: "250px" }}>
          <div className="container-fluid">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-md-row">
      <Sidebar />

      <main className="flex-grow-1 p-4" style={{ marginLeft: "250px" }}>
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <h2 className="fw-bold mb-1">Dashboard Admin</h2>
              <p className="text-muted mb-0">
                Vue d’ensemble de l’activité de THE BRIDGE.
              </p>
            </div>

            <Link to="/" className="btn btn-outline-primary">
              🌐 Voir le site
            </Link>
          </div>

          {/* Stats */}
          <div className="row g-4 mb-4">
            <StatCard title="Total véhicules" value={stats.totalVehicles} color="primary" />
            <StatCard title="En vente" value={stats.vehiclesEnVente} color="success" />
            <StatCard title="En location" value={stats.vehiclesEnLocation} color="info" />
            <StatCard title="Disponibles" value={stats.vehiclesDisponibles} color="secondary" />
            <StatCard title="Réservations en attente" value={stats.reservationsEnAttente} color="warning" />
            <StatCard title="Réservations validées" value={stats.reservationsValidees} color="success" />
            <StatCard title="Marques" value={stats.totalBrands} color="dark" />
            <StatCard title="Véhicules mis en avant" value={stats.featuredCount} color="primary" />
          </div>

          {/* Actions rapides */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Actions rapides</h5>
              <div className="d-flex flex-wrap gap-3">
                <Link to="/admin/vehicles" className="btn btn-primary">
                  🚗 Ajouter / Modifier un véhicule
                </Link>
                <Link to="/admin/LocationManager" className="btn btn-outline-success">
                  📦 Vente & Location
                </Link>
                <Link to="/admin/reservations" className="btn btn-outline-warning">
                  📋 Réservations
                </Link>
                <Link to="/admin/marques" className="btn btn-outline-dark">
                  🏷️ Marques & Modèles
                </Link>
                <Link to="/admin/featured-choices" className="btn btn-outline-primary">
                  ⭐ Véhicules à mettre en avant
                </Link>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-4">
            {/* Réservations récentes */}
            <div className="col-xl-8">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Réservations récentes</h5>
                    <Link to="/admin/reservations" className="btn btn-sm btn-outline-primary">
                      Voir tout
                    </Link>
                  </div>

                  {recentReservations.length === 0 ? (
                    <p className="text-muted mb-0">Aucune réservation récente.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle">
                        <thead>
                          <tr>
                            <th>Client</th>
                            <th>Véhicule</th>
                            <th>Période</th>
                            <th>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentReservations.map((r) => (
                            <tr key={r.id}>
                              <td>
                                <div className="fw-semibold">{r.nom || "—"}</div>
                                <small className="text-muted">{r.email || "—"}</small>
                              </td>
                              <td>{r.vehicule || "—"}</td>
                              <td>
                                <small>
                                  {formatDate(r.dateDebut)} → {formatDate(r.dateFin)}
                                </small>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    r.statut === "validée"
                                      ? "bg-success"
                                      : r.statut === "refusée"
                                      ? "bg-danger"
                                      : "bg-warning text-dark"
                                  }`}
                                >
                                  {r.statut || "—"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Alertes */}
            <div className="col-xl-4">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h5 className="fw-bold mb-3">Alertes admin</h5>

                  {alerts.length === 0 ? (
                    <div className="alert alert-success mb-0">
                      Tout semble en ordre.
                    </div>
                  ) : (
                    alerts.map((alert, index) => (
                      <div key={index} className={`alert alert-${alert.type} mb-2`}>
                        {alert.text}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-4">
            {/* Véhicules mis en avant */}
            <div className="col-xl-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Véhicules mis en avant</h5>
                    <Link to="/admin/featured-choices" className="btn btn-sm btn-outline-primary">
                      Modifier
                    </Link>
                  </div>

                  {featuredVehicles.length === 0 ? (
                    <p className="text-muted mb-0">Aucun véhicule mis en avant.</p>
                  ) : (
                    <div className="row g-3">
                      {featuredVehicles.map((v, index) => (
                        <div key={v.id} className="col-md-6 col-xl-4">
                          <div className="card h-100 border-primary">
                            <img
                              src={v.imageUrls?.[0] || "/assets/default.jpg"}
                              alt={`${v.marque} ${v.modele}`}
                              className="card-img-top"
                              style={{ height: "170px", objectFit: "cover" }}
                            />
                            <div className="card-body">
                              <span className="badge bg-primary mb-2">
                                Position {index + 1}
                              </span>
                              <h6 className="fw-bold mb-1">
                                {v.marque} {v.modele}
                              </h6>
                              <p className="text-muted small mb-2">
                                {v.annee} • {v.ficheTechnique?.carburant || "—"}
                              </p>
                              <p className="fw-bold text-primary mb-0">
                                {formatPrice(v.prix)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Derniers véhicules */}
            <div className="col-xl-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Derniers véhicules ajoutés</h5>
                    <Link to="/admin/vehicles" className="btn btn-sm btn-outline-primary">
                      Gérer
                    </Link>
                  </div>

                  {recentVehicles.length === 0 ? (
                    <p className="text-muted mb-0">Aucun véhicule trouvé.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle">
                        <thead>
                          <tr>
                            <th>Véhicule</th>
                            <th>Vente</th>
                            <th>Location</th>
                            <th>Prix</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentVehicles.map((v) => (
                            <tr key={v.id}>
                              <td>
                                <div className="fw-semibold">
                                  {v.marque} {v.modele}
                                </div>
                                <small className="text-muted">{v.annee || "—"}</small>
                              </td>
                              <td>
                                {v.enVente ? (
                                  <span className="badge bg-success">Oui</span>
                                ) : (
                                  <span className="badge bg-secondary">Non</span>
                                )}
                              </td>
                              <td>
                                {v.location?.estDisponible ? (
                                  <span className="badge bg-success">Oui</span>
                                ) : (
                                  <span className="badge bg-secondary">Non</span>
                                )}
                              </td>
                              <td>{formatPrice(v.prix)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ title, value, color = "primary" }) {
  return (
    <div className="col-6 col-md-4 col-xl-3">
      <div className={`card border-0 shadow-sm h-100 border-start border-4 border-${color}`}>
        <div className="card-body">
          <div className="text-muted small mb-2">{title}</div>
          <div className={`fw-bold fs-3 text-${color}`}>{value}</div>
        </div>
      </div>
    </div>
  );
}
